import { Injectable } from "@angular/core";
import algoliasearch, { SearchClient, SearchIndex } from "algoliasearch";



interface InitParams {
  globalApiKey: string;
  scopedApiKey: string;
  organizationId: string;
  defaultIndexName: string;
}

interface AnonymousInitParams {
  apiKey: string;
  defaultIndexName: string;
}

/**
 * Global is root org + org specific data
 * Scoped is only org specific data
 */
export type AlgoliaSdkType = 'global' | 'scoped' | 'anonymous';

@Injectable({
  providedIn: "root"
})
export class AlgoliaService {

  private organizationId: string;


  private sdkInstances: Record<AlgoliaSdkType, SearchClient | null> = {
    global: null,
    scoped: null,
    anonymous: null,
  };

  private indexes: Record<AlgoliaSdkType, SearchIndex | null> = {
    global: null,
    scoped: null,
    anonymous: null,
  };

  init({
    globalApiKey,
    scopedApiKey,
    organizationId,
    defaultIndexName
  }: InitParams) {
    this.organizationId = organizationId;

    this.sdkInstances.global = algoliasearch(
      'ROHEAAZM43',
      globalApiKey,
    );
    this.sdkInstances.scoped = algoliasearch(
      'ROHEAAZM43',
      scopedApiKey,
    );
    this.indexes.global = this.sdkInstances.global.initIndex(defaultIndexName);
    this.indexes.scoped = this.sdkInstances.scoped.initIndex(defaultIndexName);
  }

  initAnonymous({ apiKey, defaultIndexName }: AnonymousInitParams) {
    this.sdkInstances.anonymous = algoliasearch('ROHEAAZM43', apiKey);
    this.indexes.anonymous = this.sdkInstances.anonymous.initIndex(defaultIndexName);
  }

  /** Will search through the orgs index, and the root org id index */
  searchGlobalIndex: SearchIndex['search'] = (query, requestOpts) => {
    const index = this.indexes.global;

    if (index === null) {
      throw new Error(`[AlgoliaService:getObject] service not initialized for sdkType=global`);
    }

    return index.search(query, {
      ...requestOpts,
      userToken: this.organizationId
    });
  }

  searchScopedIndex: SearchIndex['search'] = (query, requestOpts) => {
    const index = this.indexes.scoped;

    if (index === null) {
      throw new Error(`[AlgoliaService:getObject] service not initialized for sdkType=scoped`);
    }

    return index.search(query, {
      ...requestOpts,
      userToken: this.organizationId
    });
  }

  searchAnonymousIndex: SearchIndex['search'] = (query, requestOpts) => {
    const index = this.indexes.anonymous;

    if (index === null) {
      throw new Error('[AlgoliaService:searchAnonymousIndex] service not initialized');
    }

    return index.search(query, requestOpts);
  }

  getObjectIdsFilter(objectIds: string[]) {
    return objectIds.map(objectId => `objectID:${objectId}`).join(' OR ');
  }

  async getObject<TObject>(objectID: string, sdkType: AlgoliaSdkType = 'global'): Promise<TObject> {
    const index = this.indexes[sdkType];

    if (index === null) {
      throw new Error(`[AlgoliaService:getObject] service not initialized for sdkType=${sdkType}`);
    }

    return index.getObject<TObject>(objectID);
  }
}
